import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { motion } from 'motion/react';
import AnimatedLogo from './AnimatedLogo';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, TooltipProvider } from '../ui/Tooltip';
import {
  Building2,
  Waves,
  FolderKanban,
  LogOut,
  Bell,
  GitFork,
  Users,
  BookOpen,
  Search,
} from 'lucide-react';
import {
  authAPI,
  getCurrentUser,
  notificationsAPI,
  USER_STORAGE_UPDATED_EVENT,
} from '../../services/api';
import { userGithubAvatarUrl } from '../../utils/githubAvatarUrl';
import { FeatureFlagProvider, FeatureGate } from '../../contexts/FeatureFlagContext';
import FloatingSidebar from '../FloatingSidebar/FloatingSidebar';
import AIChat from '../AIChat/AIChat';
import CommandPalette from '../CommandPalette/CommandPalette';
import PageGuard from '../PageGuard/PageGuard';
import { getReleaseDateLabel } from '../../config/releaseDate';

/**
 * Primary app routes in the top bar (horizontal scroll rail on narrow viewports).
 * @param {{ isActive: (path: string) => boolean }} props
 */
function TopNavPrimaryLinks({ isActive }: any) {
  const linkClass = (path) => {
    const active = isActive(path);
    return [
      'nav-link-mdl shrink-0 whitespace-nowrap rounded-sm border-b-2',
      'text-slate-400 hover:text-slate-200 hover:border-brand-purple',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface',
      active
        ? 'text-brand-purple border-brand-purple hover:text-brand-purple hover:border-brand-purple'
        : 'border-transparent',
    ].join(' ');
  };

  return (
    <>
      <FeatureGate flag="ui.pages.Dashboard">
        <Link to="/" className={linkClass('/')} aria-current={isActive('/') ? 'page' : undefined}>
          <Building2 className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Organisation
        </Link>
      </FeatureGate>
      <FeatureGate flag="ui.pages.Streams">
        <Link
          to="/streams"
          className={linkClass('/streams')}
          aria-current={isActive('/streams') ? 'page' : undefined}
        >
          <Waves className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Streams
        </Link>
      </FeatureGate>
      <FeatureGate flag="ui.pages.Projects">
        <Link
          to="/projects"
          className={linkClass('/projects')}
          aria-current={isActive('/projects') ? 'page' : undefined}
        >
          <FolderKanban className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Projects
        </Link>
      </FeatureGate>
      <FeatureGate flag="ui.pages.Repositories">
        <Link
          to="/repositories"
          className={linkClass('/repositories')}
          aria-current={isActive('/repositories') ? 'page' : undefined}
        >
          <GitFork className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Repositories
        </Link>
      </FeatureGate>
      <FeatureGate flag="ui.pages.Members">
        <Link
          to="/members"
          className={linkClass('/members')}
          aria-current={isActive('/members') ? 'page' : undefined}
        >
          <Users className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          Members
        </Link>
      </FeatureGate>
    </>
  );
}

const toolbarIconBtnClass =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-dark-border bg-dark-elevated text-slate-400 transition-all duration-200 ease-out hover:bg-dark-base hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface';

const toolbarIconLinkClass = (active) =>
  `toolbar-icon-link-mdl ${toolbarIconBtnClass} ${
    active
      ? 'border-brand-purple/40 bg-brand-purple/15 text-brand-purple-light hover:bg-brand-purple/20 hover:text-brand-purple-light'
      : ''
  }`;

const navIconMotion = {
  whileHover: { scale: 1.08 },
  whileTap: { scale: 0.92 },
  transition: { type: 'spring' as const, stiffness: 500, damping: 25 },
};

const MotionLink = motion.create(Link);

function ProfileNavAvatar({ avatarUrl, initial }: any) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [avatarUrl]);
  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt=""
        width={44}
        height={44}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return <span className="relative z-10 text-lg font-semibold">{initial}</span>;
}

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userStorageRev, setUserStorageRev] = useState(0);

  useEffect(() => {
    const onUserStorage = () => setUserStorageRev((n) => n + 1);
    window.addEventListener(USER_STORAGE_UPDATED_EVENT, onUserStorage);
    return () => window.removeEventListener(USER_STORAGE_UPDATED_EVENT, onUserStorage);
  }, []);

  const user = getCurrentUser();
  const profileAvatarUrl = userGithubAvatarUrl(user) || user?.googlePictureUrl || null;
  const profileInitial = user?.username?.[0]?.toUpperCase() || 'U';
  const layoutBg = '/assets/layoutbackground.svg';
  const [unreadCount, setUnreadCount] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    notificationsAPI
      .getUnreadCount()
      .then((r) => setUnreadCount(r.count ?? 0))
      .catch(() => setUnreadCount(0));
  }, [location.pathname]);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleLogout = () => {
    authAPI.logout();
    navigate('/');
  };

  const isNavActive = useCallback(
    (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path)),
    [location.pathname]
  );

  const docsActive = location.pathname === '/docs';
  const notificationsActive = location.pathname === '/notifications';
  const releaseDateLabel = getReleaseDateLabel();

  return (
    <FeatureFlagProvider>
      <TooltipProvider>
        <div
          className="relative min-h-screen bg-dark-base"
          style={{
            backgroundImage: `url(${layoutBg})`,
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% auto',
            backgroundAttachment: 'fixed',
          }}
        >
          <a
            href="#main-content"
            className="absolute left-[-9999px] top-4 z-[60] rounded-lg border border-dark-border bg-dark-elevated px-4 py-2 text-sm font-medium text-brand-purple-light shadow-lg focus:left-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50"
          >
            Skip to content
          </a>

          <header className="fixed top-0 left-0 right-0 z-50 border-b border-dark-border bg-dark-surface/95 shadow-sm backdrop-blur-md">
            {releaseDateLabel ? (
              <span
                className="absolute left-0 top-0 z-[51] pl-1.5 pt-0.5 text-[9px] font-normal tabular-nums leading-none text-slate-500/60 sm:pl-2 sm:text-[10px]"
                title={`Release ${releaseDateLabel}`}
              >
                {releaseDateLabel}
              </span>
            ) : null}
            <nav aria-label="Main navigation" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
              <div className="flex h-14 min-h-[3.5rem] items-center justify-between gap-2 sm:h-16 sm:min-h-[4rem]">
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                  <FeatureGate flag="ui.pages.Dashboard">
                    <div className="flex shrink-0 items-center">
                      <Link
                        to="/"
                        className="nav-logo-mdl flex items-center rounded-lg"
                        aria-label="VantagePoint home"
                      >
                        <span className="flex h-9 w-[9.5rem] shrink-0 items-center sm:h-12 sm:w-[12.25rem] md:h-14 md:w-[14.25rem]">
                          <AnimatedLogo className="h-full w-full object-contain object-left" />
                        </span>
                      </Link>
                    </div>
                  </FeatureGate>

                  <div className="nav-rail-mdl min-w-0">
                    <TopNavPrimaryLinks isActive={isNavActive} />
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                  <Tooltip content="Command palette (⌘K)">
                    <motion.button
                      type="button"
                      className={toolbarIconBtnClass}
                      onClick={() => setPaletteOpen(true)}
                      aria-label="Open command palette"
                      {...navIconMotion}
                    >
                      <Search className="h-4 w-4" aria-hidden />
                    </motion.button>
                  </Tooltip>
                  <Tooltip content="Documentation">
                    <MotionLink
                      to="/docs"
                      className={toolbarIconLinkClass(docsActive)}
                      aria-label="Documentation"
                      {...navIconMotion}
                    >
                      <BookOpen className="h-5 w-5" aria-hidden />
                    </MotionLink>
                  </Tooltip>
                  <FeatureGate flag="ui.pages.Notifications">
                    <Tooltip content="Notifications">
                      <MotionLink
                        to="/notifications"
                        className={`relative ${toolbarIconLinkClass(notificationsActive)}`}
                        aria-label="Notifications"
                        {...navIconMotion}
                      >
                        <Bell className="h-5 w-5" aria-hidden />
                        {unreadCount > 0 && (
                          <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-vivid-red px-1 text-[10px] font-bold text-white">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </MotionLink>
                    </Tooltip>
                  </FeatureGate>
                  <Tooltip content="Log out">
                    <motion.button
                      type="button"
                      onClick={handleLogout}
                      className={toolbarIconBtnClass}
                      aria-label="Log out"
                      {...navIconMotion}
                    >
                      <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                    </motion.button>
                  </Tooltip>
                  <FeatureGate flag="ui.pages.Profile">
                    <Tooltip content="Account settings">
                      <MotionLink
                        key={`profile-nav-${user?.id ?? 'x'}-${userStorageRev}`}
                        to="/settings/account/profile"
                        className={`toolbar-icon-link-mdl relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dark-border shadow-sm transition-all duration-200 ease-out hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-surface sm:h-11 sm:w-11 ${
                          location.pathname.startsWith('/settings/account')
                            ? 'ring-2 ring-brand-purple ring-offset-2 ring-offset-dark-surface'
                            : profileAvatarUrl
                              ? 'bg-dark-elevated ring-1 ring-dark-border'
                              : 'bg-brand-purple text-white'
                        }`}
                        aria-label="Account settings"
                        {...navIconMotion}
                      >
                        <ProfileNavAvatar avatarUrl={profileAvatarUrl} initial={profileInitial} />
                      </MotionLink>
                    </Tooltip>
                  </FeatureGate>
                </div>
              </div>
            </nav>
          </header>

          <FloatingSidebar />

          <FeatureGate flag="ui.ai.chat">
            <AIChat />
          </FeatureGate>

          <main
            id="main-content"
            className="relative z-10 mx-auto max-w-7xl px-3 pb-6 pt-14 sm:px-6 sm:pt-16 lg:px-8"
          >
            <PageGuard>
              <Suspense
                fallback={
                  <div className="px-6 py-12 text-sm text-slate-400">Loading…</div>
                }
              >
                <Outlet />
              </Suspense>
            </PageGuard>
          </main>
          <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
      </TooltipProvider>
    </FeatureFlagProvider>
  );
};

export default Layout;
